/**
 * PWA Install Prompt Component
 * Shows a prompt to install the app when available
 */
import { useState, useEffect, useCallback } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const InstallPWA = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(() => {
    // Check if already installed on initial render
    if (typeof window !== 'undefined') {
      return window.matchMedia('(display-mode: standalone)').matches;
    }
    return false;
  });
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS] = useState(() => {
    // Check if iOS on initial render
    if (typeof window !== 'undefined') {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }
    return false;
  });
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  const handleBeforeInstall = useCallback((e) => {
    e.preventDefault();
    setInstallPrompt(e);
    
    // Show prompt after a delay (don't be annoying on first visit)
    const hasSeenPrompt = localStorage.getItem('pwa-prompt-seen');
    const lastDismissed = localStorage.getItem('pwa-prompt-dismissed');
    
    if (!hasSeenPrompt) {
      setTimeout(() => {
        setShowPrompt(true);
        localStorage.setItem('pwa-prompt-seen', 'true');
      }, 30000); // Show after 30 seconds
    } else if (lastDismissed) {
      const daysSinceDismissed = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed > 7) {
        setShowPrompt(true);
      }
    }
  }, []);

  const handleAppInstalled = useCallback(() => {
    setIsInstalled(true);
    setShowPrompt(false);
    setInstallPrompt(null);
  }, []);

  useEffect(() => {
    // Skip if already installed
    if (isInstalled) return;

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, handleBeforeInstall, handleAppInstalled]);

  const handleInstall = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!installPrompt) {
      // Fallback - show iOS-like instructions for unsupported browsers
      setShowIOSInstructions(true);
      return;
    }

    try {
      const result = await installPrompt.prompt();
      console.log('Install prompt result:', result);

      if (result.outcome === 'accepted') {
        setIsInstalled(true);
      }
    } catch (err) {
      console.error('Install error:', err);
    }
    
    setShowPrompt(false);
    setInstallPrompt(null);
  };

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowPrompt(false);
    setShowIOSInstructions(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    localStorage.setItem('ios-prompt-seen', 'true');
  };

  const handleShowIOSInstructions = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowIOSInstructions(true);
  };

  const handleCloseIOSInstructions = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowIOSInstructions(false);
  };

  // Don't show if already installed
  if (isInstalled) return null;

  // iOS Instructions Modal
  if (showIOSInstructions) {
    return (
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        onClick={handleCloseIOSInstructions}
      >
        <div 
          className="bg-white dark:bg-slate-800 rounded-2xl p-6 max-w-sm w-full border border-teal-300 dark:border-teal-600 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Install on Your Device</h3>
            <button 
              onClick={handleCloseIOSInstructions}
              onTouchEnd={handleCloseIOSInstructions}
              className="text-gray-500 hover:text-gray-800 dark:hover:text-white p-2 -m-2 touch-manipulation"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold text-sm flex-shrink-0">
                1
              </div>
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Tap the Share button</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">At the bottom of Safari (square with arrow) or Menu in Chrome</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold text-sm flex-shrink-0">
                2
              </div>
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Scroll down and tap</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">&quot;Add to Home Screen&quot; or &quot;Install App&quot;</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center text-teal-600 dark:text-teal-400 font-semibold text-sm flex-shrink-0">
                3
              </div>
              <div>
                <p className="text-gray-800 dark:text-white font-medium">Tap Add</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">The app will appear on your home screen</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleCloseIOSInstructions}
            onTouchEnd={handleCloseIOSInstructions}
            className="w-full mt-6 bg-teal-600 hover:bg-teal-700 text-white h-12 text-base touch-manipulation"
          >
            Got it!
          </Button>
        </div>
      </div>
    );
  }

  // Main install prompt
  if (!showPrompt && !isIOS) return null;

  // iOS users - check if we should show prompt
  if (isIOS && !showPrompt) {
    const hasSeenIOSPrompt = localStorage.getItem('ios-prompt-seen');
    if (hasSeenIOSPrompt) return null;
    // Auto-show for iOS after delay
    setTimeout(() => setShowPrompt(true), 5000);
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-[9998] animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border-2 border-teal-300 dark:border-teal-600 shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-teal-200 dark:from-teal-900/50 dark:to-teal-800/50 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-teal-600 dark:text-teal-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-800 dark:text-white">Install H E Direct</h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Add to home screen for quick access
            </p>
          </div>
          
          <button 
            onClick={handleDismiss}
            onTouchEnd={handleDismiss}
            className="text-gray-500 hover:text-gray-800 dark:hover:text-white p-2 -m-2 touch-manipulation flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex gap-2 mt-4">
          {isIOS ? (
            <Button
              onClick={handleShowIOSInstructions}
              onTouchEnd={handleShowIOSInstructions}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-11 touch-manipulation"
            >
              <Download className="w-4 h-4 mr-2" />
              How to Install
            </Button>
          ) : (
            <Button
              onClick={handleInstall}
              onTouchEnd={handleInstall}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white h-11 touch-manipulation"
            >
              <Download className="w-4 h-4 mr-2" />
              Install App
            </Button>
          )}
          <Button
            onClick={handleDismiss}
            onTouchEnd={handleDismiss}
            variant="outline"
            className="border-gray-300 dark:border-slate-600 text-gray-600 dark:text-gray-300 h-11 touch-manipulation"
          >
            Later
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
